import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Download, RefreshCw, Search, DollarSign, AlertCircle, Filter } from 'lucide-react';
import PermissionGate from '@/components/rbac/PermissionGate';
import { csvExportService } from '@/services/csvExportService';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { format } from 'date-fns';

interface Transaction {
  id: string;
  user_id: string;
  entity_id: string;
  entity_type: string;
  amount: string;
  currency: string;
  transaction_type: string;
  payment_provider: string | null;
  payment_provider_transaction_id: string | null;
  status: string;
  description: string | null;
  metadata: any;
  created_at: string;
  updated_at: string;
}

const AdminTransactionManagement = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [refundingId, setRefundingId] = useState<string | null>(null);
  const [selectedRefund, setSelectedRefund] = useState<Transaction | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadTransactions();
    setupRealtimeSubscription();

    return () => {
      supabase.channel('admin-transactions').unsubscribe();
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transactions, searchTerm, statusFilter, typeFilter, providerFilter]);

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel('admin-transactions')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions'
        },
        () => {
          loadTransactions();
        }
      )
      .subscribe();

    return channel;
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      
      // Bypass TypeScript until Supabase types regenerate
      const { data, error } = await (supabase as any)
        .from('transactions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setTransactions((data as any) || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load transactions',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(txn => 
        txn.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        txn.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        txn.entity_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        txn.payment_provider_transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        txn.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(txn => txn.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(txn => txn.transaction_type === typeFilter);
    }

    // Provider filter
    if (providerFilter !== 'all') {
      filtered = filtered.filter(txn => txn.payment_provider === providerFilter);
    }

    setFilteredTransactions(filtered);
  };

  const handleRefund = async (transaction: Transaction) => {
    setSelectedRefund(transaction);
  };

  const confirmRefund = async () => {
    if (!selectedRefund) return;

    try {
      setRefundingId(selectedRefund.id);

      // Bypass TypeScript until Supabase types regenerate
      const { error } = await (supabase as any)
        .from('transactions')
        .update({ 
          status: 'refunded',
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRefund.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Transaction ${selectedRefund.id.substring(0, 8)}... has been marked as refunded`
      });

      setSelectedRefund(null);
      loadTransactions();
    } catch (error) {
      console.error('Error refunding transaction:', error);
      toast({
        title: 'Error',
        description: 'Failed to process refund',
        variant: 'destructive'
      });
    } finally {
      setRefundingId(null);
    }
  };

  const handleExportCSV = () => {
    const headers = [
      'Transaction ID',
      'User ID',
      'Entity Type',
      'Entity ID',
      'Amount',
      'Currency',
      'Type',
      'Provider',
      'Provider Transaction ID',
      'Status',
      'Description',
      'Created At',
      'Updated At'
    ];

    const data = filteredTransactions.map(txn => ({
      'Transaction ID': txn.id,
      'User ID': txn.user_id,
      'Entity Type': txn.entity_type,
      'Entity ID': txn.entity_id,
      'Amount': txn.amount,
      'Currency': txn.currency,
      'Type': txn.transaction_type,
      'Provider': txn.payment_provider || 'N/A',
      'Provider Transaction ID': txn.payment_provider_transaction_id || 'N/A',
      'Status': txn.status,
      'Description': txn.description || 'N/A',
      'Created At': format(new Date(txn.created_at), 'yyyy-MM-dd HH:mm:ss'),
      'Updated At': format(new Date(txn.updated_at), 'yyyy-MM-dd HH:mm:ss')
    }));

    const csv = csvExportService.convertToCSV(data, headers);
    csvExportService.downloadCSV(
      csv,
      `transactions-export-${format(new Date(), 'yyyy-MM-dd')}.csv`
    );

    toast({
      title: 'Success',
      description: `Exported ${filteredTransactions.length} transactions to CSV`
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      completed: 'bg-green-600/20 text-green-400 border-green-600/30',
      pending: 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30',
      failed: 'bg-red-600/20 text-red-400 border-red-600/30',
      refunded: 'bg-gray-600/20 text-gray-400 border-gray-600/30'
    };

    return (
      <Badge className={variants[status] || 'bg-gray-600/20 text-gray-400'}>
        {status}
      </Badge>
    );
  };

  const stats = {
    total: transactions.length,
    completed: transactions.filter(t => t.status === 'completed').length,
    pending: transactions.filter(t => t.status === 'pending').length,
    refunded: transactions.filter(t => t.status === 'refunded').length,
    totalRevenue: transactions
      .filter(t => t.status === 'completed')
      .reduce((sum, t) => sum + parseFloat(t.amount.toString()), 0)
  };

  return (
    <PermissionGate permission="manage_venue">
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Transaction Management</h1>
            <p className="text-gray-400 mt-1">View, filter, and manage all platform transactions</p>
          </div>
          <Button
            onClick={loadTransactions}
            variant="outline"
            className="border-sports-green text-sports-green hover:bg-sports-green hover:text-black"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Transactions</p>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                </div>
                <DollarSign className="h-8 w-8 text-sports-green" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <div>
                <p className="text-sm text-gray-400">Completed</p>
                <p className="text-2xl font-bold text-green-400">{stats.completed}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <div>
                <p className="text-sm text-gray-400">Pending</p>
                <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <div>
                <p className="text-sm text-gray-400">Refunded</p>
                <p className="text-2xl font-bold text-gray-400">{stats.refunded}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <div>
                <p className="text-sm text-gray-400">Total Revenue</p>
                <p className="text-2xl font-bold text-sports-green">
                  ${stats.totalRevenue.toFixed(2)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-900 border-gray-700 text-white"
                />
              </div>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                  <SelectItem value="booking">Booking</SelectItem>
                  <SelectItem value="one-time">One-time</SelectItem>
                  <SelectItem value="refund">Refund</SelectItem>
                </SelectContent>
              </Select>

              <Select value={providerFilter} onValueChange={setProviderFilter}>
                <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                  <SelectValue placeholder="Provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Providers</SelectItem>
                  <SelectItem value="stripe">Stripe</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <p className="text-sm text-gray-400">
                Showing {filteredTransactions.length} of {transactions.length} transactions
              </p>
              <Button
                onClick={handleExportCSV}
                variant="outline"
                size="sm"
                className="border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white"
                disabled={filteredTransactions.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">All Transactions</CardTitle>
            <CardDescription>Complete transaction history across the platform</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12 text-gray-400">Loading transactions...</div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">No transactions found</p>
                <p className="text-sm text-gray-500 mt-2">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-700">
                      <TableHead className="text-gray-300">Transaction ID</TableHead>
                      <TableHead className="text-gray-300">Date</TableHead>
                      <TableHead className="text-gray-300">Entity</TableHead>
                      <TableHead className="text-gray-300">Type</TableHead>
                      <TableHead className="text-gray-300">Amount</TableHead>
                      <TableHead className="text-gray-300">Provider</TableHead>
                      <TableHead className="text-gray-300">Status</TableHead>
                      <TableHead className="text-gray-300">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTransactions.map((txn) => (
                      <TableRow key={txn.id} className="border-gray-700">
                        <TableCell className="font-mono text-sm text-gray-300">
                          {txn.id.substring(0, 8)}...
                        </TableCell>
                        <TableCell className="text-gray-300">
                          {format(new Date(txn.created_at), 'MMM dd, yyyy HH:mm')}
                        </TableCell>
                        <TableCell className="text-gray-300">
                          <div className="text-sm">
                            <div className="font-medium">{txn.entity_type}</div>
                            <div className="text-gray-500 text-xs">{txn.entity_id.substring(0, 8)}...</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-300 capitalize">
                          {txn.transaction_type.replace('-', ' ')}
                        </TableCell>
                        <TableCell className="text-gray-300 font-semibold">
                          {txn.currency} {parseFloat(txn.amount.toString()).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-gray-300 capitalize">
                          {txn.payment_provider || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(txn.status)}
                        </TableCell>
                        <TableCell>
                          {txn.status === 'completed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRefund(txn)}
                              disabled={refundingId === txn.id}
                              className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                            >
                              {refundingId === txn.id ? 'Processing...' : 'Refund'}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Refund Confirmation Dialog */}
        <AlertDialog open={!!selectedRefund} onOpenChange={() => setSelectedRefund(null)}>
          <AlertDialogContent className="bg-gray-800 border-gray-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Confirm Refund</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-300">
                Are you sure you want to refund this transaction?
                {selectedRefund && (
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Amount:</span>
                      <span className="text-white font-semibold">
                        {selectedRefund.currency} {parseFloat(selectedRefund.amount.toString()).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Transaction ID:</span>
                      <span className="text-white font-mono text-xs">
                        {selectedRefund.id.substring(0, 16)}...
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Provider:</span>
                      <span className="text-white capitalize">
                        {selectedRefund.payment_provider || 'N/A'}
                      </span>
                    </div>
                  </div>
                )}
                <p className="mt-4 text-yellow-400 text-xs">
                  Note: This will mark the transaction as refunded in the system. You may need to 
                  process the actual refund through your payment provider separately.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmRefund}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Confirm Refund
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </PermissionGate>
  );
};

export default AdminTransactionManagement;
